import React, { useEffect, useState } from 'react';
import { HiOutlineStop } from 'react-icons/hi2';
import { MdDelete } from 'react-icons/md';
import { PiPackage, PiTruck, PiWarning } from 'react-icons/pi';
import { Button, Input, Text, Title } from 'rizzui';
import { toast } from 'sonner';
import CustomSelect from '../../../../components/forms/CustomSelect';
import ModalForm from '../../../../components/forms/ModalForm';
import useProducts from '../../../../hooks/api-calls/useProducts';
import useHandleErrors from '../../../../hooks/useHandleErrors';
import { PickupService } from '../../../../services/pickup/pickupService';
import { Pickup } from '../../../../type/entities/pickup-type';
import { SuggestedProduct } from '../../../../type/entities/suggested-product-type';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../redux/store';

interface FinishDailyPickupsModalProps {
    routeGroups: any;
    refetchData: () => void;
}

const FinishDailyPickupsModal: React.FC<FinishDailyPickupsModalProps> = ({
    routeGroups,
    refetchData
}) => {
    // STATES
    const { user } = useSelector((state: RootState) => state.auth);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [activePickups, setActivePickups] = useState<Pickup[]>([]);
    const [canFinishDay, setCanFinishDay] = useState(false);
    const [returnProducts, setReturnProducts] = useState<SuggestedProduct[]>([]);

    // SERVICES
    const pickupService = new PickupService();
    const { handleErrors } = useHandleErrors();
    const { getProductsList } = useProducts();

    // FUNCTIONS

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES FINALIZAR JORNADA DIARIA
     * @EN FINISH DAILY PICKUPS
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleFinishPickups = async () => {
        setLoading(true);
        
        // Filtrar solo recogidas completadas
        const completedPickups = activePickups.filter((pickup: Pickup) => 
            pickup.pickupStatus === 'completed'
        );

        // Validación: verificar que hay recogidas completadas para enviar
        if (completedPickups.length === 0) {
            toast.error('No hay recogidas completadas para finalizar la jornada');
            setLoading(false);
            return;
        }

        const sendValues = {
            userId: user?.id || '',
            pickups: completedPickups.map((pickup: Pickup) => pickup.id),
            products: returnProducts.map((product: SuggestedProduct) => ({
                id: product.productId,
                name: product.productName,
                type: 'in',
                quantity: product.totalEstimatedQuantity,
            }))
        };

        try {
            const response = (await pickupService.finishDailyPickups(sendValues)).getResponseData();

            if (response.success) {
                toast.success('Jornada diaria finalizada con éxito');
                refetchData();
                setIsOpen(false);
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        } finally {
            setLoading(false);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES VERIFICAR SI SE PUEDE FINALIZAR LA JORNADA
     * @EN CHECK IF DAY CAN BE FINISHED
     */
    //-------------------------------------------------------------------------------------------------------------
    const checkCanFinishDay = (pickups: Pickup[]) => {
        if (!pickups || pickups.length === 0) {
            setCanFinishDay(false);
            return;
        }

        // NUEVA LÓGICA: Solo verificar que hay al menos una recogida completada
        // No importa el estado de las otras recogidas
        const hasCompletedPickups = pickups.some(pickup => 
            pickup.pickupStatus === 'completed'
        );


        // Permitir finalizar si hay al menos una recogida completada
        // Las recogidas no completadas simplemente no se incluirán en la finalización
        setCanFinishDay(hasCompletedPickups);
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTENER RECOGIDAS ACTIVAS DE LOS GRUPOS DE RUTAS
     * @EN GET ACTIVE PICKUPS FROM ROUTE GROUPS
     */
    //-------------------------------------------------------------------------------------------------------------
    useEffect(() => {
        if (routeGroups && routeGroups.length > 0) {
            const allPickups = routeGroups.flatMap((group: any) =>
                group.pickups.filter((pickup: Pickup) =>
                    pickup.pickupStatus !== 'cancelled' && pickup.pickupStatus !== 'rescheduled'
                )
            );
            setActivePickups(allPickups);
            checkCanFinishDay(allPickups);
        } else {
            setActivePickups([]);
            setCanFinishDay(false);
        }
    }, [routeGroups]);
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTENER ESTADÍSTICAS DE RECOGIDAS
     * @EN GET PICKUP STATISTICS
     */
    //-------------------------------------------------------------------------------------------------------------
    const getPickupStats = () => {
        const completed = activePickups.filter(p => p.pickupStatus === 'completed').length;
        const failed = activePickups.filter(p => p.pickupStatus === 'failed').length;
        const pending = activePickups.filter(p =>
            p.pickupStatus === 'planned' || p.pickupStatus === 'signature_pending'
        ).length;

        return { completed, failed, pending, total: activePickups.length };
    };
    //-------------------------------------------------------------------------------------------------------------

    const stats = getPickupStats();

    // No mostrar el botón si no hay recogidas activas
    if (!activePickups.length) {
        return null;
    }

    return (
        <>
            {/* Botón para finalizar jornada diaria - solo visible cuando todas las recogidas estén completadas o fallidas */}
            {canFinishDay && (
                <Button
                    onClick={() => setIsOpen(true)}
                    variant='solid'
                    color='danger'
                    className="bg-red-600 hover:bg-red-700 text-white font-bold"
                >
                    <HiOutlineStop className="h-4 w-4 mr-2" />
                    Finalizar Jornada
                </Button>
            )}

            {/* Modal principal */}
            <ModalForm
                title="Finalizar Recogidas Diarias"
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                containerClassName="!max-w-4xl"
            >
                {loading ? (
                    <div className="flex justify-center items-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <Text className="ml-3">Procesando...</Text>
                    </div>
                ) : (
                    <form className="space-y-6">
                        {/* Resumen de recogidas */}
                        <div className="bg-blue-50 p-4 rounded-lg">
                            <div className="flex items-center mb-3">
                                <PiTruck className="h-5 w-5 text-blue-600 mr-2" />
                                <Title as="h4" className="text-blue-900">
                                    Resumen de Recogidas ({activePickups.length})
                                </Title>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="bg-white p-3 rounded border">
                                    <Text className="text-2xl font-bold text-green-800">
                                        {getPickupStats().completed}
                                    </Text>
                                    <Text className="text-sm text-green-600">Completadas</Text>
                                </div>
                                <div className="bg-white p-3 rounded border">
                                    <Text className="text-2xl font-bold text-red-800">
                                        {getPickupStats().failed}
                                    </Text>
                                    <Text className="text-sm text-red-600">Fallidas</Text>
                                </div>
                            </div>
                        </div>

                        {/* Advertencia */}
                        <div className="space-y-4">
                            <div className="flex items-center justify-center mb-4">
                                <div className="bg-red-100 p-3 rounded-full">
                                    <PiWarning className="h-8 w-8 text-red-600" />
                                </div>
                            </div>

                            <div className="text-center">
                                <Title as="h4" className="text-gray-900 mb-2">
                                    ¿Estás seguro de finalizar la jornada?
                                </Title>
                                <Text className="text-gray-600 mb-4">
                                    Esta acción marcará como finalizada las recogidas como finalizadas.
                                    No podrás realizar más cambios después de confirmar.
                                </Text>

                                <div className="bg-gray-50 p-3 rounded-lg mb-4">
                                    <Text className="text-sm text-gray-700">
                                        <strong>Resumen final:</strong><br />
                                        {/* • {stats.completed} recogidas completadas<br />
                                        • {stats.failed} recogidas fallidas<br /> */}
                                        • {stats.total} recogidas en total
                                    </Text>
                                </div>
                            </div>
                        </div>

                        {/* Productos que regresan al almacén */}
                        <div className="bg-white md:p-2 rounded-lg">
                            <div className="flex flex-col md:flex-row items-center justify-between mb-4">
                                <div className="flex items-center">
                                    <PiPackage className="h-5 w-5 text-green-600 mr-2" />
                                    <Title as="h4" className="text-green-900">
                                        Productos que regresan al almacén
                                    </Title>
                                </div>
                                <Text className="text-sm text-gray-600">
                                    Productos no entregados que vuelven al almacén
                                </Text>
                            </div>

                            <CustomSelect
                                id="returnProducts"
                                label="Productos"
                                placeholder="Seleccionar productos que regresan"
                                className='bg-white'
                                containerClassName='mb-3'
                                options={getProductsList()}
                                value={
                                    getProductsList().filter((option: any) =>
                                        returnProducts.some((p) => p.productId === option.value)
                                    ) || null
                                }
                                onChange={(value: any) => {
                                    const selectedIds: string[] = value?.map((v: any) => v.value) || [];
                                    const updated: SuggestedProduct[] = selectedIds.map((id) => {
                                        const existing = returnProducts.find((p) => p.productId === id);
                                        return existing ? existing : {
                                            productId: id,
                                            productName: value.find((v: any) => v.value === id)?.label || '',
                                            totalEstimatedQuantity: 1,
                                            type: 'in'
                                        };
                                    });
                                    setReturnProducts(updated);
                                }}
                                isClearable={true}
                                isSearchable={true}
                                isLoading={!getProductsList()}
                                isMulti={true}
                            />

                            {returnProducts.map((product: SuggestedProduct) => (
                                <div key={product.productId} className="flex items-center justify-between py-2 border-b">
                                    <div className="flex items-center flex-row gap-3">
                                        <Button
                                            onClick={() => {
                                                setReturnProducts(
                                                    returnProducts.filter((p) => p.productId !== product.productId)
                                                );
                                            }}
                                            className="bg-red-500 text-white hover:bg-red-700 py-0 px-2 md:py-2 md:px-4"
                                        >
                                            <MdDelete className="h-5 w-5 text-white" />
                                        </Button>
                                        <Text className="text-gray-900 font-medium text-sm md:text-base">
                                            {product.productName}
                                        </Text>
                                    </div>
                                    <Input
                                        id={`return-quantity-${product.productId}`}
                                        type="number"
                                        value={product.totalEstimatedQuantity}
                                        onChange={(e) => {
                                            const newQuantity = Number(e.target.value);
                                            setReturnProducts(
                                                returnProducts.map((p) =>
                                                    p.productId === product.productId
                                                        ? { ...p, totalEstimatedQuantity: newQuantity }
                                                        : p
                                                )
                                            );
                                        }}
                                        className="w-16 text-center bg-white"
                                        min={1}
                                    />
                                </div>
                            ))}

                            {returnProducts.length === 0 && (
                                <div className="text-center py-8">
                                    <Text className="text-gray-500">
                                        No hay productos seleccionados
                                    </Text>
                                </div>
                            )}
                        </div>

                        {/* Botones de acción */}
                        <div className="flex justify-end space-x-3 py-4 border-t">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setIsOpen(false)}
                                disabled={loading}
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="button"
                                onClick={handleFinishPickups}
                                isLoading={loading}
                                disabled={loading || !canFinishDay}
                                className="flex items-center bg-red-600 hover:bg-red-700 text-white"
                            >
                                <HiOutlineStop className="h-4 w-4 mr-2" />
                                Finalizar Jornada
                            </Button>
                        </div>
                    </form>
                )}
            </ModalForm>

        </>
    );
};

export default FinishDailyPickupsModal;